// ---------------------------------------------------------------------------
// Minimal DOM helpers. No framework — shared building blocks for every screen
// in this portal rather than a per-view reimplementation of the same table
// rendering, sorting, and formatting logic.
// ---------------------------------------------------------------------------

/** Escape text destined for an innerHTML template. */
export function esc(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Create an element with attributes and children in one call. */
export function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === false || value === null || value === undefined) continue;
    if (key === 'class') node.className = value;
    else if (key === 'html') node.innerHTML = value;
    else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value);
    } else node.setAttribute(key, value === true ? '' : String(value));
  }
  for (const child of children.flat()) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

/** Replace a container's contents. */
export function render(container, ...nodes) {
  container.replaceChildren(...nodes.flat().filter(Boolean));
}

/**
 * Format an ISO timestamp for display, or an em dash when absent. Fixed to
 * en-GB (day-month-year, 24h clock) rather than the viewer's locale — this is
 * a shared admin tool, and dates in screenshots/support threads should read
 * the same for every operator regardless of browser locale.
 */
export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Date only, no time — for summary stat cells where the time isn't useful. */
export function fmtDateOnly(iso, timeZone) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Access-window dates (trial / CMAI Core) are entered in the org's timezone
  // (start 00:00, end 23:59 there), so callers pass it to show the same
  // calendar day the operator picked. Omitted = the browser's own zone.
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) });
}

/** Activity-feed timestamp: just the time for something that happened today, otherwise "Mon DD". */
export function fmtActivityWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    : d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
}

/** Human-friendly relative time, used for "expires in ...". */
export function fmtUntil(iso) {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'expired';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} days`;
}

/**
 * Clickable `<th>` for server-side sorting. `state` is a mutable {sortBy,
 * sortDir} object owned by the caller; clicking toggles direction on the
 * active column or switches column (defaulting to ascending), then calls
 * `onSort()` so the caller can re-fetch. Kept generic across tables (orgs,
 * org members, all-users) rather than duplicated per view.
 */
export function sortableHeader(state, field, label, onSort, extraClass = '') {
  const active = state.sortBy === field;
  const arrow = active ? (state.sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  return el(
    'th',
    { class: extraClass },
    el(
      'button',
      {
        type: 'button',
        class: 'th-sort',
        title: active
          ? `Sorted by ${label}, ${state.sortDir === 'asc' ? 'ascending' : 'descending'}. Click to reverse the order.`
          : `Sort the table by ${label}. Changes the order only — no data is modified.`,
        onclick: () => {
          if (state.sortBy === field) {
            state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
          } else {
            state.sortBy = field;
            state.sortDir = 'asc';
          }
          onSort();
        },
      },
      label + arrow,
    ),
  );
}

/** OrgRole words that are acronyms, not names — title-casing them reads as a typo. */
const ROLE_ACRONYMS = new Set(['PMO']);

/** Mirrored by roleLabel() in api/src/modules/users/users.service.ts, which names
 *  the role in the "added to organisation" email — keep the two in step. */
export function roleLabel(role) {
  return String(role)
    .split('_')
    .map((w) => (ROLE_ACRONYMS.has(w) ? w : w.charAt(0) + w.slice(1).toLowerCase()))
    .join(' ');
}

/** Up-to-two-letter avatar initials from a full name ("Sam Owner" -> "SO"). */
export function initials(fullName) {
  const parts = String(fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Status badges for one organisation-membership row — org-detail.js's member
 * table and users.js's all-users table both render a person's row from the
 * same shape (membershipActive, accountActive, activated, removedAt,
 * accountDeletedAt — see listUsersForOrg/listAllUsers in the API), and both
 * used to compute "Active" from `activated` alone, ignoring the other two
 * isActive flags entirely. That's what let a membership- or account-level
 * deactivation still read as "Active" on screen while the person was blocked
 * at login — the backend's login gate checks accountActive AND
 * membershipActive, this didn't. One shared function so the two tables can't
 * drift out of sync with each other, or with the gate, again.
 *
 * Returns 1+ badge elements — deliberately not collapsed to one "most severe
 * wins" badge, because a row can be simultaneously true of more than one
 * cause (e.g. deactivated in this org AND deactivated account-wide), and
 * naming only one would recreate the same kind of hidden state this exists
 * to stop.
 */
export function memberStatusBadges(u) {
  // The design's dot pills. Tones: red = blocked account-wide or gone,
  // amber = switched off in this org only, blue = invited, green = can sign in,
  // gray = membership ended.
  const TONES = { removed: 'gray', danger: 'red', org: 'amber', pending: 'blue', active: 'green' };
  const badge = (tone, label, title) =>
    el(
      'span',
      { class: `badge badge--${TONES[tone]} badge--dot`, title },
      el('span', { class: 'badge__dot' }),
      label,
    );

  // Permanent, terminal states: membership or account no longer exists.
  // These are facts about existence, not about whether access is switched
  // on, so they're shown alone — nothing else can be true of a row that's
  // gone.
  if (u.removedAt !== null) {
    return [badge('removed', 'Removed', 'Removed from this organisation. Their account and any other organisation memberships are unaffected.')];
  }
  if (u.accountDeletedAt !== null) {
    return [badge('danger', 'Account deleted', 'The whole ConstructMind account was deleted, not just this membership. Restore it from the Users directory.')];
  }

  // Reversible, non-terminal states: one or more independent switches are
  // off. Each is named for what it is and where to fix it, and — per the
  // reason this function exists — more than one can be true at once, so
  // every true one is shown rather than picking a single "most important"
  // badge.
  const badges = [];
  if (!u.accountActive) {
    badges.push(
      badge(
        'danger',
        'Account deactivated',
        'Deactivated for the whole ConstructMind account — blocks every organisation they belong to, not just this one. Reactivate from the Users directory.',
      ),
    );
  }
  if (!u.membershipActive) {
    badges.push(
      badge(
        'org',
        'Inactive in this org',
        u.activated
          ? 'Switched off in this organisation specifically. Their account may still work in other organisations they belong to.'
          : 'Invited but not yet activated in this organisation — this is the normal state before they accept.',
      ),
    );
  }
  if (badges.length > 0) return badges;

  if (!u.activated) {
    return [badge('pending', 'Pending activation', 'Invited, but has not yet set a password and signed in for the first time.')];
  }
  return [badge('active', 'Active', 'Can sign in: account and membership are both switched on, and they have completed activation.')];
}

/** "2 hours ago" / "3 days ago" — for Last activity columns. Falls back to the date past 30 days. */
export function fmtRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const secs = Math.round((Date.now() - t) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return fmtDateOnly(iso);
}

/** Copy text to the clipboard, reporting success so callers can show feedback. */
export async function copy(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
